import { useMutation } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useCallback, useState } from "react";

import {
  ERROR_MESSAGES,
  stepsStateMapping,
  type RegisterStep0FormValues,
  type RegisterStep2FormValues,
  type StepsState,
} from "#app/(unauthorized)/register/(client)/_shared/register-client-utils";
import { defaultDisabledMessage } from "#app/(unauthorized)/register/_shared/register-hooks";
import { postRegistrationUser } from "#app/_api/external-bol-registration-service/post-registration-user";
import { postRegistrationVerifyIdentity } from "#app/_api/external-bol-registration-service/post-registration-verify-identity";
import { ApiError } from "#app/lib/fetchClient.utils";
import { logError } from "#app/lib/logger";

// -----------------------------------------------------------------------------

/**
 * Manages the initial verification steps (Steps 0-1) of client registration.
 * Handles personal information collection and email verification initiation.
 *
 * @returns Object containing step state, form handlers, and email for next steps
 */
export function useRegisterClientVerification(): {
  asyncError: string | null;
  currentStep: Exclude<StepsState, 2 | 3 | 4>;
  email: string;
  handleStep0Submit: (params: {
    formValues: RegisterStep0FormValues;
    setDisabledMessage: React.Dispatch<React.SetStateAction<string | null>>;
  }) => void;
  isPendingMutation: boolean;
} {
  // For toggling between steps (final steps handled in `/register/create` page)
  // 0 = Personal Information Form
  // 1 = Check Your Email Message
  const [currentStep, setCurrentStep] = useState<
    Exclude<StepsState, 2 | 3 | 4>
  >(stepsStateMapping.step0PersonalInformation);

  // For storing `email` from Step 0 to be used in Step 1
  const [email, setEmail] = useState("");

  // For showing async errors from form submission
  const [asyncError, setAsyncError] = useState<string | null>(null);

  // For verifying a user, used in Step 0
  const { mutate, isPending: isPendingMutation } = useMutation({
    mutationFn: postRegistrationVerifyIdentity,
  });

  /**
   * Handles successful submission of Step 0 form.
   * Updates email and advances to Step 1.
   * If an error occurs, it sets the appropriate error message.
   */
  const handleStep0Submit = useCallback(
    ({
      formValues,
      setDisabledMessage,
    }: {
      formValues: RegisterStep0FormValues;
      setDisabledMessage: React.Dispatch<React.SetStateAction<string | null>>;
    }) => {
      mutate(
        {
          body: {
            firstName: formValues.firstName,
            dateOfBirth: new Date(formValues.dateOfBirth).toJSON(),
            ssn: formValues.ssn,
            email: formValues.email,
            phone: formValues.phone,
            zip: formValues.zip,
          },
        },
        {
          onSuccess: (_data, variables) => {
            setAsyncError(null);
            setEmail(variables.body.email);
            setCurrentStep(stepsStateMapping.step1EmailVerification);
          },
          onError: (error) => {
            logError(error);
            // 400 Bad Request: show form-level alert on Step 0
            if (error instanceof ApiError && error.code === 400) {
              setAsyncError(ERROR_MESSAGES.genericError);
              return;
            }
            // All others: hides step 0, shows page-level alert
            setDisabledMessage(defaultDisabledMessage);
          },
        },
      );
    },
    [mutate],
  );

  return {
    asyncError,
    currentStep,
    email,
    handleStep0Submit,
    isPendingMutation,
  };
}

// -----------------------------------------------------------------------------

/**
 * Manages the final registration steps (Steps 2-4) of client registration.
 * Handles user credentials, terms acceptance, and final account creation.
 *
 * @returns Object containing step state, form values, handlers, and mutation state
 */
export function useRegisterClientRegistration(): {
  currentStep: Exclude<StepsState, 0 | 1>;
  formValues: RegisterStep2FormValues;
  asyncError: string | null;
  isPendingMutation: boolean;
  handleStep2Success: (formValues: RegisterStep2FormValues) => void;
  handleBackToStep2: () => void;
  handleRegistrationSubmit: (params: {
    setDisabledMessage: React.Dispatch<React.SetStateAction<string | null>>;
    verificationGuid: string;
  }) => void;
} {
  // For toggling between steps (previous steps handled in `/register` page)
  // 2 = User ID & Password Form
  // 3 = Terms of Use Acceptance
  // 4 = Registration Success
  const [currentStep, setCurrentStep] = useState<Exclude<StepsState, 0 | 1>>(
    stepsStateMapping.step2UserIdAndPassword,
  );

  // For storing form values from Step 2 to be used in Step 3
  // and for resetting the form when going back to Step 2
  const [formValues, setFormValues] = useState<RegisterStep2FormValues>({
    userId: "",
    friendlyName: "",
    password: "",
    confirmPassword: "",
  });

  // For storing Step 3 async errors to be used in Step 2
  const [asyncError, setAsyncError] = useState<string | null>(null);

  // For registering a user, used in Step 3
  const { mutate, isPending: isPendingMutation } = useMutation({
    mutationFn: postRegistrationUser,
  });

  /**
   * Handles successful submission of Step 2 form.
   * Updates form values and advances to Step 3.
   *
   * @param next - Form values from Step 2 to store for Step 3
   */
  const handleStep2Success = useCallback((next: RegisterStep2FormValues) => {
    setFormValues((prev) => ({ ...prev, ...next }));
    setCurrentStep(stepsStateMapping.step3TermsOfUse);
  }, []);

  /**
   * Handles going back to Step 2 from Step 3.
   * Resets password and friendly name fields in form values.
   */
  const handleBackToStep2 = useCallback(() => {
    setFormValues((prev) => ({ ...prev, password: "", friendlyName: "" }));
    setCurrentStep(stepsStateMapping.step2UserIdAndPassword);
  }, []);

  /**
   * Handles errors during registration mutation.
   * Sets appropriate error messages based on the error type and navigates accordingly.
   *
   * @param error - The error object from the mutation
   * @param setDisabledMessage - Function to set page-level disabled message
   */
  const handleRegistrationError = useCallback(
    async ({
      error,
      setDisabledMessage,
    }: {
      error: unknown;
      setDisabledMessage: React.Dispatch<React.SetStateAction<string | null>>;
    }) => {
      // Handle 503 errors differently - they don't go back to step 2
      const statusCode =
        error instanceof HTTPError ? error.response.status : null;
      if (statusCode === 503) {
        setAsyncError(null);
        setDisabledMessage(defaultDisabledMessage);
        return;
      }

      // All other errors go back to step 2
      if (statusCode === 400) {
        const responseText =
          error instanceof HTTPError ? await error.response.text() : "";
        if (responseText.toLowerCase().includes("password")) {
          setAsyncError(ERROR_MESSAGES.passwordValidationFailed(responseText));
        } else {
          setAsyncError(ERROR_MESSAGES.userCreationFailed);
        }
      } else if (statusCode === 404) {
        setAsyncError(ERROR_MESSAGES.invalidRegistrationCode);
      } else if (statusCode === 409) {
        setAsyncError(ERROR_MESSAGES.userIdTaken(formValues.userId));
      } else {
        setAsyncError(ERROR_MESSAGES.userCreationFailed);
      }
      setCurrentStep(stepsStateMapping.step2UserIdAndPassword);
    },
    [formValues.userId],
  );

  /**
   * Handles the final registration submission to create the user account.
   * Calls the mutation with form values and verification token from email.
   * Advances to Step 4 on success or handles errors appropriately.
   *
   * @param setDisabledMessage - Function to set page-level disabled message
   * @param verificationGuid - Token from email verification link
   */
  const handleRegistrationSubmit = useCallback(
    ({
      setDisabledMessage,
      verificationGuid,
    }: {
      setDisabledMessage: React.Dispatch<React.SetStateAction<string | null>>;
      verificationGuid: string;
    }) => {
      mutate(
        {
          body: {
            acceptedTsAndCs: true,
            alias: formValues.userId,
            friendlyName: formValues.friendlyName,
            password: formValues.password,
            verificationGuid,
          },
        },
        {
          onSuccess: () => {
            setAsyncError(null);
            setCurrentStep(stepsStateMapping.step4Success);
          },
          onError: async (error) => {
            logError(error);
            await handleRegistrationError({ error, setDisabledMessage });
          },
        },
      );
    },
    [
      formValues.friendlyName,
      formValues.password,
      formValues.userId,
      handleRegistrationError,
      mutate,
    ],
  );

  return {
    currentStep,
    formValues,
    asyncError,
    isPendingMutation,
    handleStep2Success,
    handleBackToStep2,
    handleRegistrationSubmit,
  };
}
